import type { KeyboardEvent } from "react";
import type { MatchProfile } from "./types";
import type { ProfileAnswers } from "../domain/answers";
import { scaleDiffs, valuesScore } from "./valuesScore";
import { affinityScore, AFFINITY_AXES, AFFINITY_WEIGHTS } from "./affinity";
import { profileAnswerOverlap } from "./profileOverlap";
import type { CommentAnchor } from "./commentStore";
import { tierOf } from "./chromePlan";
import { useT } from "../i18n";

function scoreLabelKey(score: number): string {
  if (score >= 85) return "match.scoreStrong";
  if (score >= 70) return "match.scoreSolid";
  if (score >= 55) return "match.scoreModerate";
  return "match.scoreDifferent";
}

export function MatchCard({
  profile,
  selfScales,
  selfAnswers,
  theyLikedYou,
  showScoreDetail,
  onToggleScore,
  offsetX,
  dragging,
  fullBleed = false,
  onCommentAnchor,
}: {
  profile: MatchProfile;
  selfScales: MatchProfile["scales"];
  /** Present once the viewer has answered the questionnaire. */
  selfAnswers?: ProfileAnswers;
  /** True when this person already liked you (paid / guest visibility). */
  theyLikedYou?: boolean;
  showScoreDetail: boolean;
  onToggleScore: () => void;
  offsetX: number;
  dragging: boolean;
  /** Full-viewport hero photo plane before detail scroll. */
  fullBleed?: boolean;
  /** Tap a profile section to open the ten-word comment composer. */
  onCommentAnchor?: (anchor: CommentAnchor) => void;
}) {
  const t = useT();
  const legacyScore = valuesScore(selfScales, profile.scales);
  const diffs = scaleDiffs(selfScales, profile.scales);

  // Two-stage model runs only when both sides answered. Otherwise the card
  // shows exactly what it always showed.
  const affinity =
    selfAnswers && profile.answers
      ? affinityScore(selfAnswers, profile.answers, selfScales, profile.scales)
      : null;

  const score = affinity ? affinity.total : legacyScore;
  void theyLikedYou;
  const overlap =
    selfAnswers && profile.answers
      ? profileAnswerOverlap(selfAnswers, profile.answers)
      : null;
  const rotate = offsetX / 28;
  const commentable = Boolean(onCommentAnchor);

  const anchorProps = (anchor: CommentAnchor) =>
    commentable
      ? {
          role: "button" as const,
          tabIndex: 0,
          className: "match-card__anchor",
          onClick: () => onCommentAnchor?.(anchor),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onCommentAnchor?.(anchor);
            }
          },
        }
      : {};

  return (
    <article
      className={`match-card${dragging ? " is-dragging" : ""}${fullBleed ? " match-card--bleed" : ""}`}
      data-plan={tierOf(profile)}
      style={{
        transform: `translateX(${offsetX}px) rotate(${rotate}deg)`,
      }}
    >
      <div className="match-card__hero match-card__hero--photo" aria-hidden={!fullBleed}>
        <div className="match-card__photo-plane" {...anchorProps("photo")}>
          <span className="match-card__initial">{profile.firstName.charAt(0)}</span>
          {profile.photoUrl ? (
            <img
              className="match-card__photo"
              src={profile.photoUrl}
              alt=""
              decoding="async"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
        <div
          className="match-card__score-badge"
          aria-label={t("match.scoreBadge", { score })}
        >
          <strong>{score}</strong>
          <span>/100</span>
        </div>
        <div className="match-card__identity">
          <h2>
            {profile.firstName}, {profile.age}
          </h2>
          <p>
            {profile.city} · {profile.profession}
          </p>
        </div>
      </div>

      <div className="match-card__body">
        <p className="match-card__about" {...anchorProps("about")}>
          {profile.aboutYou}
        </p>

        <p className="match-card__matching-score">
          <span>{t("match.matchingScore")}</span>
          <strong>
            {score}
            <em>/100</em>
          </strong>
        </p>

        {overlap && (overlap.agree.length > 0 || overlap.disagree.length > 0) ? (
          <div className="match-overlap">
            {overlap.agree.length > 0 ? (
              <div className="match-overlap__block match-overlap__block--agree">
                <h3 className="match-overlap__title">{t("match.overlapAgree")}</h3>
                <ul>
                  {overlap.agree.map((line) => (
                    <li key={line.key}>
                      {t(`match.overlapItems.${line.key}`, line.vars)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {overlap.disagree.length > 0 ? (
              <div className="match-overlap__block match-overlap__block--disagree">
                <h3 className="match-overlap__title">{t("match.overlapDisagree")}</h3>
                <ul>
                  {overlap.disagree.map((line) => (
                    <li key={line.key}>
                      {t(`match.overlapItems.${line.key}`, line.vars)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="match-card__meta">
          <div {...anchorProps("lookingFor")}>
            <span className="match-card__label">{t("match.cardSeeking")}</span>
            <p>{profile.lookingFor}</p>
          </div>
          <div {...anchorProps("hobbies")}>
            <span className="match-card__label">{t("match.cardHobbies")}</span>
            <p>{profile.hobbies}</p>
          </div>
        </div>

        <button type="button" className="match-score" onClick={onToggleScore}>
          <span className="match-score__num">
            {score}
            <em>/100</em>
          </span>
          <span className="match-score__copy">
            <strong>{t("match.matchingScore")}</strong>
            <em>{t(scoreLabelKey(score))}</em>
          </span>
          <span className="match-score__chev">{showScoreDetail ? "−" : "+"}</span>
        </button>

        {showScoreDetail ? (
          affinity ? (
            <ul className="match-score__detail">
              {AFFINITY_AXES.map((axis) => (
                <li key={axis}>
                  <span>
                    {t(`match.affinityAxes.${axis}`)}
                    <em className="match-score__weight">
                      {Math.round(AFFINITY_WEIGHTS[axis] * 100)}%
                    </em>
                  </span>
                  <span>{affinity.axes[axis]}</span>
                </li>
              ))}
              <li className="match-score__foot">
                <span>{t("match.characterAxisFoot")}</span>
                <span>{legacyScore}</span>
              </li>
            </ul>
          ) : (
            <ul className="match-score__detail">
              {diffs.map((d) => (
                <li key={d.key}>
                  <span>{t(`match.scaleAxes.${d.key}`)}</span>
                  <span>
                    {t("match.youThem", { self: d.self, other: d.other })}
                  </span>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>

      {Math.abs(offsetX) > 40 ? (
        <div
          className={`match-card__stamp${offsetX > 0 ? " is-like" : " is-pass"}`}
          aria-hidden
        >
          {offsetX > 0 ? t("match.stampInterest") : t("match.stampPass")}
        </div>
      ) : null}
    </article>
  );
}
